import React from "react";
import { useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import { graphqlUrl } from "utils/config";
import SettingsList from "superAdminViews/setting/SettingsList";

const ADD = gql`
  mutation createCategoryByAdmin($parent: ID, $title: String!, $photoUrl: String) {
    createCategoryByAdmin(data: { parent: $parent, title: $title, photoUrl: $photoUrl }) {
      _id
      title
    }
  }
`;

const GET = gql`
  query getCategories($limit: Int, $skip: Int, $parent: ID) {
    getCategories(pagination: { limit: $limit, skip: $skip }, filters: { parent: $parent }) {
      _id
      title
      photoUrl
      parent {
        _id
        title
      }
    }
  }
`;

const COUNT = gql`
  query getCategoriesCount {
    getCategoriesCount
  }
`;

const UPDATE = gql`
  mutation updateCategoryByAdmin($_id: ID!, $parent: ID, $title: String, $photoUrl: String) {
    updateCategoryByAdmin(_id: $_id, data: { parent: $parent, photoUrl: $photoUrl, title: $title }) {
      _id
      title
    }
  }
`;

const DELETE = gql`
  mutation deleteCategoryByAdmin($idSet: [ID!]!) {
    deleteCategoryByAdmin(idSet: $idSet) {
      _id
    }
  }
`;

const GET_ROOTS = gql`
  query getCategories {
    getCategories(filters: { parent: null }) {
      _id
      title
    }
  }
`;

/**
 * Categories - the two-level tree shops and products are filed under.
 *
 * The top level is the shop type ("Restaurant", "Purchase"); everything
 * below it is a cuisine or a department. The parent filter existed in
 * the query but had no control, so the list mixed both levels together
 * with no way to see one branch at a time.
 */
const Categories = () => {
  const { data } = useQuery(GET_ROOTS);
  const roots = (data && data.getCategories) || [];
  const parentOptions = roots.map((root) => ({ value: root._id, label: root.title }));

  return (
    <SettingsList
      title="Categories"
      description="The shop types, and the cuisines and departments filed under them."
      singular="category"
      list={{ query: GET, key: "getCategories" }}
      count={{ query: COUNT, key: "getCategoriesCount" }}
      filters={[{ name: "parent", label: "Under", options: parentOptions }]}
      create={{
        mutation: ADD,
        variables: (values) => ({
          title: values.title.trim(),
          parent: values.parent || null,
          photoUrl: values.photoUrl || null,
        }),
      }}
      update={{
        mutation: UPDATE,
        variables: (values, row) => ({
          _id: row._id,
          title: values.title.trim(),
          parent: values.parent || null,
          photoUrl: values.photoUrl || null,
        }),
      }}
      remove={{ mutation: DELETE }}
      columns={[
        {
          key: "title",
          label: "Category",
          width: "2fr",
          render: (row) => (
            <span className="settingsSwatch">
              {row.photoUrl && (
                <img className="settingsThumb" src={`${graphqlUrl}${row.photoUrl}`} alt="" />
              )}
              <span className="settingsPrimaryCell">
                <strong>{row.title}</strong>
              </span>
            </span>
          ),
        },
        {
          key: "parent",
          label: "Under",
          width: "2fr",
          render: (row) => (row.parent ? row.parent.title : "Top level"),
        },
      ]}
      fields={[
        { name: "title", label: "Name", type: "text", required: true },
        {
          name: "parent",
          label: "Under",
          type: "select",
          options: parentOptions,
          hint: "Leave empty to make this a top-level shop type.",
        },
        { name: "photoUrl", label: "Image", type: "image", folderName: "categoryImage" },
      ]}
      toValues={(row) => ({
        title: row.title,
        parent: row.parent ? row.parent._id : "",
        photoUrl: row.photoUrl || "",
      })}
    />
  );
};

export default Categories;
